import { cloneDeep, get, isEqual, isNil, nth } from "lodash";
import { searchable } from "utils/helpers/text";
import { editorText, withLabel } from "./editor-text";

export const FIELD_KINDS = {
  TEXT: "text",
  DATE: "date",
  AMOUNT: "amount",
  PHONE: "phone",
  CODE: "code",
  BOOLEAN: "boolean",
  LIST: "list",
};

export const FIELD_GROUPS = {
  MEMBER: "MEMBER",
  SPOUSE: "SPOUSE",
  CHILDREN: "CHILDREN",
  CONTRACT: "CONTRACT",
  PAYMENT: "PAYMENT",
  BROKER: "BROKER",
};

export const groupLabel = (group) => editorText(`FIELD_GROUPS.${group}`);

const RELATED_PERSONS_PATH = "project.contact.relatedPersons";
const MEMBER_TELECOMS_PATH = "project.contact.telecoms";
const CHILD_RANKS = [1, 2, 3];
const RELATIONSHIPS = { SPOUSE: "SPOUSE", CHILD: "CHILD" };
const SELF_DESCRIBED = { selfDescribed: true };

const labelKeyOf = (path) =>
  path
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

const plain = (group, path, kind = FIELD_KINDS.TEXT, extra = {}) =>
  withLabel({ path, group, kind, ...extra }, `FIELDS.${labelKeyOf(path)}`);

const memberTelecom = (system, kind = FIELD_KINDS.TEXT) =>
  withLabel(
    {
      path: `${MEMBER_TELECOMS_PATH}[system=${system}].value`,
      group: FIELD_GROUPS.MEMBER,
      kind,
      source: {
        from: { path: MEMBER_TELECOMS_PATH, where: { system }, pick: "value" },
      },
    },
    `FIELDS.TELECOM.${system}`,
  );

const spouseField = (pick, kind = FIELD_KINDS.TEXT) =>
  withLabel(
    {
      path: `${RELATED_PERSONS_PATH}[relationship=${RELATIONSHIPS.SPOUSE}].${pick}`,
      group: FIELD_GROUPS.SPOUSE,
      kind,
      source: {
        from: {
          path: RELATED_PERSONS_PATH,
          where: { relationship: RELATIONSHIPS.SPOUSE },
          pick,
        },
      },
    },
    `FIELDS.SPOUSE.${labelKeyOf(pick)}`,
  );

const childField = (rank, pick, kind = FIELD_KINDS.TEXT) =>
  withLabel(
    {
      path: `${RELATED_PERSONS_PATH}[relationship=${RELATIONSHIPS.CHILD}][${rank - 1}].${pick}`,
      group: FIELD_GROUPS.CHILDREN,
      kind,
      source: {
        from: {
          path: RELATED_PERSONS_PATH,
          where: { relationship: RELATIONSHIPS.CHILD },
          pick,
          index: rank - 1,
        },
      },
    },
    `FIELDS.CHILD.${labelKeyOf(pick)}`,
    { rank },
  );

const column = (itemKey, kind = FIELD_KINDS.TEXT) =>
  withLabel({ itemKey, kind }, `FIELDS.COLUMNS.${labelKeyOf(itemKey)}`);

const PERSON_COLUMNS = [
  column("identity.user.gender", FIELD_KINDS.CODE),
  column("identity.user.lastname"),
  column("identity.user.firstname"),
  column("identity.user.birthDate", FIELD_KINDS.DATE),
  column("identity.socialSecurityNumber", FIELD_KINDS.CODE),
  column("identity.socialRegime", FIELD_KINDS.CODE),
  column("relationship", FIELD_KINDS.CODE),
];

const CHILD_COLUMNS = [
  { pick: "identity.user.firstname" },
  { pick: "identity.user.lastname" },
  { pick: "identity.user.birthDate", kind: FIELD_KINDS.DATE },
  { pick: "identity.socialSecurityNumber", kind: FIELD_KINDS.CODE },
];

const PRODUCT_COLUMNS = [
  column("insurancePlan.name"),
  column("cost", FIELD_KINDS.AMOUNT),
  column("costDetails.adherentCost", FIELD_KINDS.AMOUNT),
  column("costDetails.spouseCost", FIELD_KINDS.AMOUNT),
  column("costDetails.childrenCost", FIELD_KINDS.AMOUNT),
  column("currency", FIELD_KINDS.CODE),
];

const { MEMBER, CHILDREN, CONTRACT, PAYMENT, BROKER } = FIELD_GROUPS;
const { TEXT, DATE, AMOUNT, PHONE, CODE, BOOLEAN, LIST } = FIELD_KINDS;

export const FIELDS = [
  plain(MEMBER, "project.contact.user.gender", CODE),
  plain(MEMBER, "project.contact.user.lastname"),
  plain(MEMBER, "project.contact.user.firstname"),
  plain(MEMBER, "project.contact.user.birthName"),
  plain(MEMBER, "project.contact.user.birthDate", DATE),
  plain(MEMBER, "project.contact.socialSecurityNumber", CODE),
  plain(MEMBER, "project.contact.socialRegime", CODE),
  plain(MEMBER, "project.contact.familySituation", CODE),
  plain(MEMBER, "project.contact.profession"),
  plain(MEMBER, "project.contact.socioProfessionalCategory", CODE),
  plain(MEMBER, "project.contact.siretNumber", CODE),
  plain(MEMBER, "project.contact.optMadelin", BOOLEAN),
  plain(MEMBER, "project.contact.politicallyExposedPerson", BOOLEAN),
  memberTelecom("EMAIL"),
  memberTelecom("PHONE", PHONE),
  memberTelecom("LANDLINE", PHONE),
  memberTelecom("ADDRESS"),
  memberTelecom("COMPLEMENT"),
  memberTelecom("ADDRESS_LOCATION"),
  memberTelecom("RESIDENCE"),
  memberTelecom("ZIP_CODE", CODE),
  memberTelecom("CITY"),
  memberTelecom("COUNTRY"),
  memberTelecom("BIRTH_CITY"),
  memberTelecom("DEPARTMENT_OF_BIRTH", CODE),
  memberTelecom("BIRTH_COUNTRY"),

  spouseField("identity.user.gender", CODE),
  spouseField("identity.user.lastname"),
  spouseField("identity.user.firstname"),
  spouseField("identity.user.birthName"),
  spouseField("identity.user.birthDate", DATE),
  spouseField("identity.socialSecurityNumber", CODE),
  spouseField("identity.socialRegime", CODE),
  spouseField("identity.profession"),

  plain(CHILDREN, RELATED_PERSONS_PATH, LIST, { items: PERSON_COLUMNS }),
  ...CHILD_RANKS.flatMap((rank) =>
    CHILD_COLUMNS.map(({ pick, kind }) => childField(rank, pick, kind)),
  ),

  plain(CONTRACT, "insurancePlan.name"),
  plain(CONTRACT, "insurancePlan.identifier", CODE),
  plain(CONTRACT, "project.contract.issuanceDate", DATE),
  plain(CONTRACT, "subscription.number", CODE),
  plain(CONTRACT, "subscription.signedDate", DATE),
  plain(CONTRACT, "quoteNumber", CODE),
  plain(CONTRACT, "project.fields.quoteValidity", DATE),
  plain(CONTRACT, "generalCost.cost", AMOUNT),
  plain(CONTRACT, "generalCost.billingPeriod", CODE),
  plain(CONTRACT, "generalCost.costDetails.adherentCost", AMOUNT),
  plain(CONTRACT, "generalCost.costDetails.spouseCost", AMOUNT),
  plain(CONTRACT, "generalCost.costDetails.childrenCost", AMOUNT),
  plain(CONTRACT, "generalCost.administrativeFees", AMOUNT),
  plain(CONTRACT, "generalCost.tax", AMOUNT),
  plain(CONTRACT, "generalCost.currency", CODE),
  plain(CONTRACT, "meta.currentDate"),
  plain(CONTRACT, "generalCosts", LIST, { items: PRODUCT_COLUMNS }),

  plain(PAYMENT, "subscription.payment.type", CODE),
  plain(PAYMENT, "subscription.payment.frequency", CODE),
  plain(PAYMENT, "subscription.payment.debitDay"),
  plain(PAYMENT, "subscription.payment.payer.IBAN", CODE),
  plain(PAYMENT, "subscription.payment.payer.BIC", CODE),
  plain(PAYMENT, "subscription.payment.payer.owner.lastName"),
  plain(PAYMENT, "subscription.payment.payer.owner.firstName"),
  plain(PAYMENT, "subscription.payment.receiver.IBAN", CODE),
  plain(PAYMENT, "subscription.payment.receiver.BIC", CODE),

  plain(BROKER, "advisor.firstname", TEXT, SELF_DESCRIBED),
  plain(BROKER, "advisor.lastname", TEXT, SELF_DESCRIBED),
  plain(BROKER, "advisor.email", TEXT, SELF_DESCRIBED),
  plain(BROKER, "advisor.phone", PHONE, SELF_DESCRIBED),
  plain(BROKER, "organization.name", TEXT, SELF_DESCRIBED),
  plain(BROKER, "organization.siretNumber", CODE, SELF_DESCRIBED),
  plain(BROKER, "organization.broker.socialReason"),
  plain(BROKER, "organization.broker.orias", CODE),
  plain(BROKER, "organization.broker.code", CODE),
  plain(BROKER, "organization.broker.rcsNumber", CODE),
  plain(BROKER, "organization.broker.rcsPlace"),
  plain(BROKER, "organization.broker.sirenNumber", CODE),
];

const FIELDS_BY_PATH = new Map(FIELDS.map((field) => [field.path, field]));

export const fieldPhrase = (field) => {
  if (field.group === MEMBER && field.kind !== LIST)
    return editorText(
      field.kind === BOOLEAN
        ? "FIELD_PHRASES.MEMBER_FLAG"
        : "FIELD_PHRASES.MEMBER",
      { label: field.label },
    );
  if (field.group === BROKER && !field.selfDescribed)
    return editorText("FIELD_PHRASES.BROKER", { label: field.label });
  return field.label;
};

export const zoneBaseName = (zone) => (zone || "").split("|")[0];

export const findFieldByPath = (path) => FIELDS_BY_PATH.get(path);

export const isKnownPath = (zone) => FIELDS_BY_PATH.has(zoneBaseName(zone));

export const fieldOfZone = (zone) => findFieldByPath(zoneBaseName(zone));

export const fieldMapping = (field) =>
  field.source ? cloneDeep(field.source) : { key: field.path };

export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";
export const DATE_TRANSFORM_OP = "date";

export const defaultFieldMapping = (field) =>
  field.kind === DATE
    ? {
        ...fieldMapping(field),
        transforms: [{ op: DATE_TRANSFORM_OP, format: DEFAULT_DATE_FORMAT }],
      }
    : fieldMapping(field);

const fromSignature = (from = {}) => ({
  path: from.path,
  where: from.where || {},
  pick: from.pick || undefined,
  index: from.index ?? 0,
});

export const findFieldForMapping = (mapping) => {
  if (!isNil(mapping?.from?.path))
    return FIELDS.find(
      (field) =>
        !isNil(field.source?.from) &&
        isEqual(fromSignature(field.source.from), fromSignature(mapping.from)),
    );
  if (isNil(mapping?.key)) return undefined;
  return findFieldByPath(mapping.key);
};

const matchesWhere = (item, where = {}) =>
  Object.entries(where).every(
    ([key, value]) => String(get(item, key)) === String(value),
  );

export const readFieldValue = (field, payload) => {
  const from = field.source?.from;
  if (isNil(from)) return get(payload, field.path);
  const picked = (get(payload, from.path) || [])
    .filter((item) => matchesWhere(item, from.where))
    .map((item) => (from.pick ? get(item, from.pick) : item));
  if (from.all) return picked;
  return nth(picked, from.index ?? 0);
};

const matchesSearch = (field, search) => {
  const query = searchable(search);
  if (!query) return true;
  return [field.label, field.path, groupLabel(field.group)].some((text) =>
    searchable(text).includes(query),
  );
};

export const fieldOptions = ({ search, kinds } = {}) =>
  Object.values(FIELD_GROUPS)
    .map((group) => ({
      key: group,
      label: groupLabel(group),
      options: FIELDS.filter(
        (field) =>
          field.group === group &&
          (isNil(kinds) || kinds.includes(field.kind)) &&
          matchesSearch(field, search),
      ).map((field) => ({ value: field.path, label: field.label, field })),
    }))
    .filter(({ options }) => options.length > 0);
